import {
  languages,
  type Languages,
} from '../../../../i18n/i18n.language-config';
import { AuStates } from '../../../../utils/AUState';
import { UsStates } from '../../../../utils/USState';

export interface AddressLabels {
  readonly address1: string;
  readonly address2: string;
  readonly address3: string;
  readonly city: string;
  readonly state: string;
  readonly postalCode: string;
  /**
   * A real postcode from that country, shown as the field's placeholder.
   *
   * Carried here rather than derived from `postalCode`, because GB and AU share the label
   * 'Postcode' and inferring the country from it gave Australian vendors a British example.
   */
  readonly postalCodeExample: string;
}

/**
 * The address field labels for a platform's country.
 *
 * <p>Mirrors the copy `LanguageForm` uses -- Town/City and Postcode for the UK,
 * State/Territory for Australia -- so migrating this page's rendering to
 * Mantine does not quietly change what a UK or AU policyholder reads.
 */
export const addressLabelsFor = (language: Languages): AddressLabels => {
  if (language === languages['en-GB']) {
    return {
      address1: 'Address 1',
      address2: 'Address 2',
      address3: 'Address 3',
      city: 'Town/City',
      state: 'State',
      postalCode: 'Postcode',
      postalCodeExample: 'SW1A 1AA',
    };
  }

  if (language === languages['en-AU']) {
    return {
      address1: 'Address 1',
      address2: 'Address 2',
      address3: 'Address 3',
      city: 'City',
      state: 'State/Territory',
      postalCode: 'Postcode',
      postalCodeExample: '2000',
    };
  }

  return {
    address1: 'Address 1',
    address2: 'Address 2',
    address3: 'Address 3',
    city: 'City',
    state: 'State',
    postalCode: 'Zip Code',
    postalCodeExample: '10001',
  };
};

/** The state/territory options for a platform's country, or none where it has no state field. */
export const stateOptionsFor = (
  language: Languages
): readonly { value: string; label: string }[] => {
  if (language === languages['en-AU']) {
    return AuStates;
  }

  if (language === languages['en-US']) {
    return UsStates;
  }

  return [];
};
